// Track memory (P1.1, SP-4) — Architecture §4.4's "decoded tracks live in SAB as PCM".
//
// A different shape from the three rings in rings.ts, on purpose. A ring is a producer/consumer
// channel: bounded, circular, built for one side to keep writing while the other keeps reading.
// Track memory is the opposite kind of thing — written **once**, completely, before the worklet
// that reads it is even constructed, then read **randomly** (forward, backward, at any rate) for
// the length of the set. There is no producer racing a consumer, so there are no atomics, no
// sequence counters, no cache-line split. It is a header plus an array, closer to a file format
// than to a channel.
//
// That "written once, before construction" property is load-bearing, not incidental. It is what
// lets the worklet-side reader treat the buffer as pure data: SharedArrayBuffers cross into
// processorOptions by reference (the same trick P0.5 uses for the rings), so by the time the
// processor exists, every sample is already in place and will never change. Nothing here needs
// to survive a write racing a read, because that race cannot happen.
//
// SP-4's question — Float32 or Int16? — is why this file supports two formats rather than one.
// Architecture §4.4 already states the trade "Int16 halves NFR-P5 pressure… at the cost of a
// cheap dequantization on read" and defers the choice to measurement. This is the layout both
// candidates share; P1.2 is where the measurement happens.

import { asByteOffset, asFrameIndex, type ByteOffset, type FrameIndex } from './branded';

/** 'SYTM' — SYnapse Track Memory. Distinct from RING_MAGIC so the two shapes can never be
 * confused if a buffer ends up somewhere it should not. */
export const TRACK_MEMORY_MAGIC = 0x53_59_54_4d;

export const TRACK_MEMORY_VERSION = 1;

/**
 * The two candidates SP-4 rules on (Architecture §4.4).
 *
 * `0` and `1` rather than a string: the format rides in the header as a plain u32, and a string
 * would mean the worklet parsing text to decide how to read a sample — allocation on the one
 * thread that must never allocate, to answer a question a single integer already answers.
 */
export const TRACK_MEMORY_FORMAT = {
  F32: 0,
  I16: 1,
} as const;

export type TrackMemoryFormat =
  (typeof TRACK_MEMORY_FORMAT)[keyof typeof TRACK_MEMORY_FORMAT];

/** Bytes per sample for each format. Int16's whole reason to exist is this being 2, not 4. */
export function bytesPerSample(format: TrackMemoryFormat): number {
  return format === TRACK_MEMORY_FORMAT.I16 ? 2 : 4;
}

/**
 * Header fields, all u32 except FRAME_COUNT which needs the range. All plain values — never
 * atomics. Nothing after construction ever mutates a header field, so `Atomics` would be
 * ceremony around a promise this file already keeps by construction order.
 *
 * One cache line (64 bytes) of headroom even though four fields need sixteen, for the same
 * reason RING_HEADER reserves a full line for its static fields: room to grow (loop points,
 * a content hash for the M9 analysis cache) without a version bump moving payload data.
 */
export const TRACK_MEMORY_HEADER = {
  /** u32 — TRACK_MEMORY_MAGIC. */
  MAGIC: 0,
  /** u32 — TRACK_MEMORY_VERSION. */
  VERSION: 4,
  /** u32 — TRACK_MEMORY_FORMAT.F32 or .I16. */
  FORMAT: 8,
  /** u32 — channels stored (always 2 today; mono files are up-mixed at decode, see track-memory.ts). */
  CHANNEL_COUNT: 12,
  /**
   * u32 — frames per channel.
   *
   * u32 headroom is generous on purpose: an 8-minute track at 48 kHz is ~23M frames, comfortably
   * inside int32 range with three orders of magnitude to spare — NFR-P5's "two 8-minute tracks"
   * is nowhere near the ceiling this field could count to.
   */
  FRAME_COUNT: 16,
  /** u32 — the AudioContext's sampleRate at decode time. Read back, never assumed (P0.5's lesson:
   * this repo's own hardware came up at 44.1 kHz, not the 48 kHz most of the plan assumes). */
  SAMPLE_RATE_HZ: 20,
} as const;

export const TRACK_MEMORY_HEADER_BYTES = 64;

export interface TrackMemorySpec {
  readonly channelCount: number;
  readonly frameCount: number;
  /**
   * Plain `number`, not the narrower `TrackMemoryFormat` union — matching `RingLayoutSpec`'s own
   * `version: number` (P0.2). The format is genuinely unchecked at this boundary: it can arrive
   * from a stale header, a hand-built test fixture, or JS with no type checker at all, and the
   * validation below is what turns a bad one into a thrown error instead of silent misreads. A
   * narrower type here would make TypeScript "prove" that validation dead code, which is exactly
   * backwards — the check exists because the type cannot be trusted at this exact seam.
   */
  readonly format: number;
}

export interface TrackMemoryLayout extends TrackMemorySpec {
  readonly headerBytes: number;
  readonly payloadByteOffset: ByteOffset;
  readonly bytesPerSample: number;
  /** Bytes for one channel's full run — frameCount × bytesPerSample. */
  readonly channelBytes: number;
  readonly totalBytes: number;
}

/**
 * Validates a spec and derives its byte layout. Mirrors `computeRingLayout`'s shape (P0.2) for
 * the same reason: called once, off the real-time path, so it is free to be thorough — every
 * throw here is a bug that would otherwise be silent memory corruption on the audio thread.
 */
export function computeTrackMemoryLayout(spec: TrackMemorySpec): TrackMemoryLayout {
  const { channelCount, frameCount, format } = spec;

  if (!Number.isInteger(channelCount) || channelCount < 1) {
    throw new RangeError(
      `track memory: channelCount must be a positive integer, got ${String(channelCount)}`,
    );
  }
  if (!Number.isInteger(frameCount) || frameCount < 0) {
    throw new RangeError(
      `track memory: frameCount must be a non-negative integer, got ${String(frameCount)}`,
    );
  }
  if (format !== TRACK_MEMORY_FORMAT.F32 && format !== TRACK_MEMORY_FORMAT.I16) {
    throw new RangeError(`track memory: unknown format ${String(format)}`);
  }

  const sampleBytes = bytesPerSample(format);
  // Each channel is padded to a multiple of 4 bytes so the *next* channel's data starts
  // 4-byte aligned — required for the Float32Array/Int16Array views the reader builds per
  // channel to be legal at all, and true automatically for F32 (4 bytes/sample) but not for
  // I16 (2 bytes/sample) when frameCount is odd.
  const rawChannelBytes = frameCount * sampleBytes;
  const channelBytes = Math.ceil(rawChannelBytes / 4) * 4;

  return {
    channelCount,
    frameCount,
    format,
    headerBytes: TRACK_MEMORY_HEADER_BYTES,
    payloadByteOffset: asByteOffset(TRACK_MEMORY_HEADER_BYTES),
    bytesPerSample: sampleBytes,
    channelBytes,
    totalBytes: TRACK_MEMORY_HEADER_BYTES + channelCount * channelBytes,
  };
}

/**
 * Where channel `channel`'s data begins.
 *
 * Planar — `[channel0: frame0..frameN][channel1: frame0..frameN]` — the same convention
 * `audioChannelByteOffset` uses for the audio ring, for the reason stated there: Web Audio hands
 * out one array per channel already, so planar is a straight copy per channel rather than a
 * per-sample interleaving loop that benefits nobody.
 */
export function trackChannelByteOffset(
  layout: TrackMemoryLayout,
  channel: number,
): ByteOffset {
  return asByteOffset(layout.payloadByteOffset + channel * layout.channelBytes);
}

/** A frame index, clamped into `[0, frameCount)`. Reading past either end is a caller bug the
 * layout can catch cheaply — the reader itself must stay branch-light on the hot path instead. */
export function clampFrameIndex(layout: TrackMemoryLayout, frame: number): FrameIndex {
  if (layout.frameCount === 0) {
    return asFrameIndex(0);
  }
  return asFrameIndex(Math.min(layout.frameCount - 1, Math.max(0, frame)));
}
